import { Component } from 'react'
import { Lock } from 'lucide-react'

/**
 * Catches render-time errors in a subtree so a JS crash surfaces as a
 * friendly card instead of an empty <body>. QA hit 'Patient Portal
 * opens a blank page' twice (07/09 + 07/22) — the first pass fixed
 * the destructuring crash, but any *other* runtime error further down
 * the tree still left the whole subtree unmounted with no signal. An
 * ErrorBoundary is the last line of defence so a bad payload / prop /
 * effect can't nuke the whole page silently.
 *
 * Usage:
 *   <ErrorBoundary fallback={({ error }) => (...)}>
 *     <PatientPortalPage />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends Component {
  state = { error: null }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Surface in console so we can diagnose from a shared session
    // recording. Prod builds strip the readable messages via minify;
    // the stack still comes through.
    console.error('ErrorBoundary caught:', error, info)
  }

  reset = () => this.setState({ error: null })

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback({ error: this.state.error, reset: this.reset })
      }
      return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#06080E' }}>
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: '#FF5C5C15', border: '1px solid #FF5C5C30' }}>
              <Lock size={28} style={{ color: '#FF5C5C' }} />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Something went wrong</h1>
            <p className="text-sm mb-4" style={{ color: '#8A99B4' }}>
              This page couldn't be loaded. Please refresh, or try again in a moment.
            </p>
            <button onClick={() => window.location.reload()}
              className="btn-primary text-sm">Reload</button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
